import type { AppliedFrame, AppliedTouchedRow, AppliedRowIdentity, AppliedRow as Row } from './applied-frame';
import { encounterCreatureField } from '../../shared/array-types';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { readItemField, readItemFieldReference } from '../compile/item-field-reader';
import { creatureAppearanceDocument } from '../authoring/creature-appearance';
import { applyResourceValues, type ResourceValues } from '../resources/resource-adjust';
import { observeActionComputed } from '../compile/action-computed-delta';
import { statusRows } from '../statuses/expiry';
import { compilePictureRow } from '../compile/picture-row-construction';
import { sameJson } from '../compile/json-change';
import { projectCardInventory } from './card-edit-inventory';

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const resources = ['v_current', 'v_max', 'v_temp', 'v_max_temp', 'aps', 'aps_total', 'mana'] as const;
function sameColumn(left: unknown, right: unknown): boolean {
    if (left == null && right == null) return true;
    if (left !== null && right !== null && typeof left !== 'object' && typeof right !== 'object') return String(left) === String(right);
    return sameJson(left, right);
}
function inventory(host: Row, id: string): Row | null {
    if (upper(readInventoryField(host, 'inventory_id')) === id) return host;
    for (const key of ['inventory', 'inventory_in_container', 'encounter_inventories_arr'] as const) for (const row of readInventoryRows(host, key)) {
        const found = inventory(row, id); if (found) return found;
    }
    return null;
}
/** Status editors also serve carried and nested inventory targets. */
export function* cardEditStatusRows(host: Row): Generator<Row> {
    yield* statusRows(host, 'target'); yield* statusRows(host, 'caster');
    for (const key of ['inventory', 'inventory_in_container', 'encounter_inventories_arr'] as const)
        for (const row of readInventoryRows(host, key)) yield* cardEditStatusRows(row);
}

/** A card save carries one saved row and its affected computed leaves. Definitions
 * and all unrelated computed clusters remain borrowed from the held host. */
export function savedCardEditFrame(identity: { operation_id: string; encounter_id: string; actor_id: string },
    saved: readonly AppliedTouchedRow[], hosts: readonly Row[], deletes: readonly AppliedRowIdentity[] = [],
    pictures: readonly Row[] = [], cdnUrl?: string): AppliedFrame {
    const retired = saved.filter(entry => entry.table === 'statuses' && Number(readItemField(entry.row, 'status_deleted')) === 1);
    const rows = saved.filter(entry => !retired.includes(entry)).map(entry => {
        const id = upper(entry.row_id);
        const held = entry.table === 'encounter_creatures' ? hosts.find(host =>
            upper(encounterCreatureField(record(host.encounter_creatures), 'encounter_creature_id')) === id)
            : entry.table === 'inventory' ? hosts.map(host => inventory(host, id)).find(Boolean)
                : entry.table === 'statuses' ? hosts.flatMap(host => [...cardEditStatusRows(host)])
                    .find(row => upper(readItemField(row, 'status_id')) === id) : null;
        const row = Object.fromEntries(Object.entries(entry.row).flatMap(([key, saved]) => {
            const value = entry.table === 'encounter_creatures' && key === 'appearance' ? creatureAppearanceDocument(saved) : saved;
            const previous = !held ? undefined : entry.table === 'encounter_creatures' ? encounterCreatureField(record(held.encounter_creatures), key)
                : entry.table === 'inventory' ? readInventoryField(held, key) : readItemFieldReference(held, key);
            const identity = entry.table === 'inventory' ? 'inventory_id' : entry.table === 'statuses' ? 'status_id' : 'encounter_creature_id';
            return held && key !== identity && sameColumn(value, previous) ? [] : [[key, value]];
        }));
        return { ...entry, row };
    });
    const frame: AppliedFrame = { kind: 'applied', ...identity, rev: 0, member_revs: {}, computed: {}, computed_delta: {},
        new_entities: [], deletes: [...deletes, ...retired.map(({ table, row_id }) => ({ table, row_id }))], rows };
    const changed = new Set([...rows, ...frame.deletes].map(row => row.row_id.toUpperCase()));
    const owners = hosts.filter(host => {
        const id = upper(encounterCreatureField(record(host.encounter_creatures), 'encounter_creature_id'));
        return id && (changed.has(id) || [...changed].some(key => inventory(host, key))
            || [...cardEditStatusRows(host)].some(row => changed.has(upper(readItemField(row, 'status_id')))));
    }).map(host => upper(encounterCreatureField(record(host.encounter_creatures), 'encounter_creature_id')));
    for (const id of owners) frame.computed_delta![id] = { set: [], unset: [] };
    projectCardInventory(frame, hosts);
    const media = new Map(pictures.map(row => [upper(readItemField(row, 'picture_id')), row]));
    if (media.size) frame.inputs = { card_edit_pictures: pictures };
    for (const saved of rows) {
        if (!['encounter_creatures', 'inventory'].includes(saved.table)) continue;
        const id = upper(saved.row_id);
        const held = saved.table === 'encounter_creatures' ? hosts.find(host =>
            upper(encounterCreatureField(record(host.encounter_creatures), 'encounter_creature_id')) === id)
            : hosts.map(host => inventory(host, id)).find(Boolean);
        if (!held) continue;
        const pictureEntries = Object.entries(saved.row).filter(([key]) => key.endsWith('_pic_id')).flatMap(([key, value]) => {
            const picture = media.get(upper(value));
            if (!picture || !cdnUrl) return [];
            const projected = compilePictureRow(picture, { cdnUrl });
            const bucket = key.replace(/_pic_id$/, '_picture');
            return [[bucket, [projected]] as const];
        });
        const pictureSets: Array<{ path: string[]; value: unknown }> = pictureEntries.length ? (record(held.pictures)
            ? pictureEntries.map(([bucket, value]) => ({ path: ['pictures', bucket], value }))
            : [{ path: ['pictures'], value: Object.fromEntries(pictureEntries) }]) : [];
        if (pictureEntries.length && saved.table === 'encounter_creatures') {
            const ec = record(held.encounter_creatures);
            pictureSets.push(...(record(ec?.pictures)
                ? pictureEntries.map(([bucket, value]) => ({ path: ['encounter_creatures', 'pictures', bucket], value }))
                : [{ path: ['encounter_creatures', 'pictures'], value: Object.fromEntries(pictureEntries) }]));
        }
        if (pictureSets.length) frame.computed_delta![id] = { ...frame.computed_delta![id] ?? { set: [], unset: [] }, mirrors: { set: pictureSets, unset: [] } };
        if (saved.table === 'encounter_creatures' && 'encounter_creature_name' in saved.row && record(held.items)
            && !record(held.player_characters)) {
            const name = readItemField(saved.row, 'encounter_creature_name');
            const delta = frame.computed_delta![id] ?? { set: [], unset: [] };
            frame.computed_delta![id] = { ...delta, mirrors: { set: [...delta.mirrors?.set ?? [], { path: ['items', 'item_name'],
                value: name ?? readItemField(record(held.items), 'item_name') }], unset: delta.mirrors?.unset ?? [] } };
        }
        const values = Object.fromEntries(resources.filter(key => key in saved.row)
            .map(key => [key, Number(readItemField(saved.row, key) ?? 0)])) as ResourceValues;
        if (!Object.keys(values).length) continue;
        const key = saved.table, computed = record(held.computed), vitality = record(computed?.vitality);
        const catalog = record(held.catalog) ?? hosts.filter(host => saved.table !== 'inventory' || inventory(host, id))
            .map(host => record(host.catalog)).find(Boolean);
        const trial: Row = { [key]: { ...record(held[key]) }, items: held.items, player_characters: held.player_characters,
            statuses: held.statuses, catalog,
            ...(computed ? { computed: vitality ? { vitality: { ...vitality } } : {} } : {}) };
        const finish = observeActionComputed([[id, trial]], new Map([[id, { computed: [['vitality']], mirrors: [],
            replaceMirrors: [[key, 'aps_used'], [key, 'mana_percent']] }]]));
        applyResourceValues(trial, values, undefined, null, key === 'inventory' ? catalog ?? undefined : undefined);
        for (const [id, delta] of Object.entries(finish())) {
            const previous = frame.computed_delta![id];
            frame.computed_delta![id] = previous ? { ...delta, set: [...previous.set, ...delta.set], unset: [...previous.unset, ...delta.unset], mirrors: { set: [...previous.mirrors?.set ?? [], ...delta.mirrors?.set ?? []],
                unset: [...previous.mirrors?.unset ?? [], ...delta.mirrors?.unset ?? []] } } : delta;
        }
    }
    return frame;
}
